/**
 * NOVA assets server functions — coach auth plus delegation to
 * `~/lib/server/nova/assets`. One call answers the whole My assets / My
 * content surface, one answers a single asset's page (or one item's), one
 * lists a collection's items, one lists every content collection's items
 * for My content, one reads What NOVA knows, and one lists the work she
 * holds for the win-back panel.
 *
 * Aliases: nova assets function, coach assets api, my assets api
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireNovaCoachAuth } from "~/lib/coach-middleware";
import { isAssetKey, type AssetKey } from "~/lib/shared/nova/assets/catalog";

// Type-only re-exports, erased at build, so a route component never imports
// from `~/lib/server`.
export type {
  CoachAssetNote,
  CoachAssets,
  CoachAssetView,
} from "~/lib/server/nova/assets/coach-assets";
export type {
  CoachCollection,
  CoachCollectionItem,
  CoachContentCollection,
  ConversationCollectionItem,
} from "~/lib/server/nova/assets/collection";
export type {
  AssetDetailLive,
  AssetDetailVersion,
  AssetHistoryEvent,
  AssetMarketCheck,
  CoachAssetDetail,
  MovedAssetItem,
} from "~/lib/server/nova/assets/detail";
export type {
  CoachHeldWork,
  HeldAsset,
} from "~/lib/server/nova/assets/held-work";
export type {
  StartAssetNavigation,
  StartAssetRefusal,
  StartAssetResult,
  StartAssetSearch,
} from "~/lib/server/nova/assets/start";
export type {
  NovaKnowsLine,
  NovaKnowsRecord,
  NovaKnowsRecordKey,
} from "~/lib/shared/nova/knows";
export type {
  AssetKey,
  AssetStage,
  AssetSurface,
  CasComponentKey,
} from "~/lib/shared/nova/assets/catalog";
export type {
  AssetAccount,
  AssetStatus,
  AssetTarget,
} from "~/lib/shared/nova/assets/status";

export const getNovaAssets = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }) => {
    const { getCoachAssets } =
      await import("~/lib/server/nova/assets/coach-assets");
    return getCoachAssets(context.coach);
  });

/**
 * The boundary check for an asset key. `AssetKey` is derived from the catalog
 * itself, so validating against `isAssetKey` cannot fall out of step with the
 * entries the way a second hand-written list of twenty keys would.
 */
export const novaAssetKeySchema = z.object({
  assetKey: z.custom<AssetKey>(
    (value) => typeof value === "string" && isAssetKey(value),
    { message: "Unknown asset key" },
  ),
});

/**
 * One asset's page: the version to show, the live one, its history and the
 * conversation working on it. A read only — it never starts anything.
 *
 * `itemId` scopes the read to one item of a collection (one LinkedIn post).
 * Null when the coach owns no such item, and `{ movedTo }` when a post opened
 * by its row id has since joined a lineage.
 */
export const getNovaAssetDetail = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .validator(
    novaAssetKeySchema.extend({ itemId: z.string().uuid().optional() }),
  )
  .handler(async ({ data, context }) => {
    const { getCoachAssetDetail } =
      await import("~/lib/server/nova/assets/detail");
    return getCoachAssetDetail(context.coach, data.assetKey, data.itemId);
  });

/** One collection's items, newest first, for an item's page. */
export const getNovaAssetCollection = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .validator(novaAssetKeySchema)
  .handler(async ({ data, context }) => {
    const { getCoachCollection } =
      await import("~/lib/server/nova/assets/collection");
    return getCoachCollection(context.coach, data.assetKey);
  });

/**
 * Every content collection's items for My content, and whether the coach may
 * open them. Listed even when she may not: the page shows them locked.
 */
export const getNovaContent = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }) => {
    const { listCoachContent } =
      await import("~/lib/server/nova/assets/collection");
    return listCoachContent(context.coach);
  });

/**
 * What the coach holds in NOVA whatever she can open: approved, written and
 * never approved, and when the last of it was written. Her cards can name
 * each asset Kept or Frozen from this same evidence, but not how many
 * LinkedIn posts or what the date was — the win-back panel reads this for
 * that richer, per-asset accounting.
 */
export const getNovaHeldWork = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }) => {
    const { getCoachHeldWork } =
      await import("~/lib/server/nova/assets/held-work");
    return getCoachHeldWork(context.coach);
  });

/**
 * What NOVA knows about the coach, record by record. A record her access does
 * not open comes back with its key only; the page lists it locked.
 */
export const getNovaKnows = createServerFn({ method: "GET" })
  .middleware([requireNovaCoachAuth])
  .handler(async ({ context }) => {
    const { readWhatNovaKnows } = await import("~/lib/server/nova/knows");
    return readWhatNovaKnows(context.coach);
  });

/**
 * Opens one asset: the skill, Plan step, agent or page behind it, started
 * directly. Returns either where to navigate or a typed refusal — it never
 * throws for a refusal, only for a denied subscription (the same
 * `NovaAccessDeniedError` `createNovaConversation` throws, which the client's
 * paywall navigation already recognises).
 *
 * `itemId` names one item of a collection to revise; the server checks that
 * the coach owns it.
 */
export const startNovaAsset = createServerFn({ method: "POST" })
  .middleware([requireNovaCoachAuth])
  .validator(
    novaAssetKeySchema.extend({ itemId: z.string().uuid().optional() }),
  )
  .handler(async ({ data, context }) => {
    const { startAsset } = await import("~/lib/server/nova/assets/start");
    return startAsset({
      coach: context.coach,
      assetKey: data.assetKey,
      itemId: data.itemId,
    });
  });
